import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Arrow } from 'app/components/Common/Arrow'
import { LabelCounter } from 'app/components/Common/LabelCounter'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles } from 'app/theme'
import { useKeenSlider } from 'keen-slider/react'
import React, { memo, useState } from 'react'

import { Item, Props as ItemProps } from './Item'

export interface Props {
  bg?: string
  counter?: string
  description?: string
  drawVariant?: string
  items?: ItemProps[]
  label?: string
  noHoverSlide?: boolean
  title?: string
  variant?: string
}

export const RoomsCard = memo(function RoomsCard({
  counter,
  items,
  label,
  title,
  description,
}: Props) {
  if (!items) {
    return null
  }

  const [currentSlide, setCurrentSlide] = useState(0)
  const [sliderLeft, instanceRefLeft] = useKeenSlider({
    loop: true,
    drag: false,
    initial: 0,
    defaultAnimation: {
      duration: 1500,
    },

    breakpoints: {
      '(max-width: 1199px)': {
        slides: { perView: 1.2, spacing: 18, origin: 'auto' },
        drag: true,
      },
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel)
      let index = s.track.details.rel
    },
  })
  const [sliderRight, instanceRefRight] = useKeenSlider({
    loop: true,
    drag: false,
    initial: 1,
    defaultAnimation: {
      duration: 1500,
    },
  })

  return (
    <Container>
      <Top>
        {label ? (
          <FadeInUp delay={0.4}>
            <LabelCounter label={label} counter={counter} variant="dark" />
          </FadeInUp>
        ) : null}
        {title ? <Title tag="h3" length={30} text={title} /> : null}
        {description ? (
          <FadeInUp delay={0.6}>
            <Description dangerouslySetInnerHTML={{ __html: description }} />
          </FadeInUp>
        ) : null}
      </Top>

      <Wrapper dial={1} row space="between">
        <LeftSlider>
          <FadeInUp>
            <SliderImage ref={sliderLeft} className="keen-slider">
              {items.map((item, index) => (
                <SlideImage key={index} className="keen-slider__slide">
                  <Item {...item} index={index} />
                </SlideImage>
              ))}
            </SliderImage>
          </FadeInUp>
        </LeftSlider>

        <RightSlider>
          {items.length > 1 ? (
            <FadeInUp delay={0.6}>
              <CounterWrapper dial={4} row>
                {items.map((item: any, index: number) => (
                  <Counter
                    key={index}
                    className={`${currentSlide === index ? 'active' : ''}`}
                    onClick={() => {
                      const newIndex = (index + items.length) % items.length
                      instanceRefLeft.current?.moveToIdx(newIndex)
                      instanceRefRight.current?.moveToIdx(newIndex + 1)
                    }}
                  >
                    {`0${index + 1}`}
                  </Counter>
                ))}
              </CounterWrapper>
            </FadeInUp>
          ) : null}

          <FadeInUp>
            <SliderImage ref={sliderRight} className="keen-slider">
              {items.map((item, index) => (
                <SlideImage
                  key={(index + 1) % items.length}
                  className="keen-slider__slide"
                >
                  <Item {...item} />
                </SlideImage>
              ))}
              {items.length > 1 ? (
                <Arrows>
                  <Arrow
                    direction="R"
                    onClick={(e) => {
                      e.stopPropagation()
                      instanceRefLeft.current?.next()
                      instanceRefRight.current?.next()
                    }}
                  />
                  <Arrow
                    onClick={(e) => {
                      e.stopPropagation()
                      instanceRefLeft.current?.prev()
                      instanceRefRight.current?.prev()
                    }}
                  />
                </Arrows>
              ) : null}
            </SliderImage>
          </FadeInUp>
        </RightSlider>
      </Wrapper>
    </Container>
  )
})

const Top = styled.div`
  max-width: 53.125rem;
  margin-bottom: 3.75rem;

  @media (max-width: 1199px) {
    padding-right: 1.5rem;
    margin-bottom: 1.5rem;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.xl}

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Description = styled.div`
  ${styles.description.dark}

  margin-top: 0.5rem;

  @media (max-width: 1199px) {
    max-width: none;
    margin-top: 0.375rem;
  }
`

const Wrapper = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  padding: 6rem 0 6rem 6.944vw;

  @media (max-width: 1199px) {
    padding: 2rem 0 2rem 1.5rem;
  }
`

const LeftSlider = styled.div`
  width: 51.944vw;
  .keen-slider {
    a > div.item-box {
      aspect-ratio: 1.78;
    }
  }

  & > div {
    height: 100%;
  }

  @media (max-width: 1199px) {
    width: 100%;
    height: auto;
  }
`

const RightSlider = styled.div`
  position: relative;
  width: 24.028vw;

  & > div {
    height: 100%;
  }

  .keen-slider {
    .keen-slider__slide {
      pointer-events: none;
    }

    a > div.item-box {
      aspect-ratio: 0.82;
    }
  }

  .item-label,
  .item-bottom {
    display: none;
  }

  @media (max-width: 1199px) {
    display: none;
  }
`
const SliderImage = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
`
const SlideImage = styled.div`
  position: relative;
  overflow: hidden;
`

const Container = styled.section`
  padding-left: 10vw;
  position: relative;
  width: 100%;
  overflow: hidden;

  @media (max-width: 1199px) {
    margin-top: 3rem;
    padding-left: 1.5rem;
  }
`
const CounterWrapper = styled(FlexBox)`
  margin-top: 2.625rem;
  margin-bottom: 4.625rem;
`

const Counter = styled.div`
  ${styles.label}

  color: ${({ theme }) => theme.colors.variants.neutralDark1};

  cursor: pointer;
  font-size: 0.75rem;
  line-height: 1rem;
  opacity: 0.3;
  transition: opacity 0.4s ease-in-out;

  &:not(:last-of-type) {
    margin-right: 1.5rem;
  }

  &:hover,
  &.active {
    opacity: 1;
  }
`

const Arrows = styled(FlexBox)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;

  > div {
    &:first-of-type {
      margin-bottom: 0.375rem;
    }
  }

  @media (max-width: 1199px) {
  }
`
